import React, { useState } from 'react';
import { webEnrichmentService } from '../../web_enrichment/services/web-enrichment.service';
import { WebEnrichmentData } from '../../web_enrichment/types';

interface PendingOverride {
  gemini_value: any;
  detected_at: string;
}

interface Props {
  pendingOverrides: Record<string, PendingOverride>;
  currentValues: Record<string, any>;
  companyId: number;
  accentColor?: string;
  onResolved: (updated: WebEnrichmentData) => void;
}

function formatFieldLabel(field: string): string {
  return field
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

function formatValue(val: any): string {
  if (val === null || val === undefined) return '—';
  if (Array.isArray(val)) {
    if (val.length === 0) return '—';
    return val
      .map((item) =>
        typeof item === 'object' ? JSON.stringify(item) : String(item)
      )
      .join(', ');
  }
  if (typeof val === 'object') {
    return JSON.stringify(val, null, 2);
  }
  return String(val);
}

function parseDetectedAt(value: string): Date | null {
  if (!value) return null;
  const d = new Date(value);
  return isNaN(d.getTime()) ? null : d;
}

export const WebOverridePendingReview: React.FC<Props> = ({
  pendingOverrides,
  currentValues,
  companyId,
  accentColor = '#3B82F6',
  onResolved,
}) => {
  const [loadingField, setLoadingField] = useState<string | null>(null);
  const [loadingAll, setLoadingAll] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [allError, setAllError] = useState<string | null>(null);

  const fields = Object.keys(pendingOverrides);

  if (fields.length === 0) return null;

  const handleAccept = async (field: string) => {
    setLoadingField(field);
    setErrors((prev) => ({ ...prev, [field]: '' }));
    try {
      const updated = await webEnrichmentService.acceptPendingOverride(companyId, field);
      onResolved(updated);
    } catch (err: any) {
      setErrors((prev) => ({
        ...prev,
        [field]: err.message || 'Failed to accept',
      }));
    } finally {
      setLoadingField(null);
    }
  };

  const handleDiscard = async (field: string) => {
    setLoadingField(field);
    setErrors((prev) => ({ ...prev, [field]: '' }));
    try {
      const updated = await webEnrichmentService.discardPendingOverride(companyId, field);
      onResolved(updated);
    } catch (err: any) {
      setErrors((prev) => ({
        ...prev,
        [field]: err.message || 'Failed to discard',
      }));
    } finally {
      setLoadingField(null);
    }
  };

  const handleAcceptAll = async () => {
    setLoadingAll(true);
    setAllError(null);
    try {
      const updated = await webEnrichmentService.acceptAllPendingOverrides(companyId);
      onResolved(updated);
    } catch (err: any) {
      setAllError(err.message || 'Failed to accept all');
    } finally {
      setLoadingAll(false);
    }
  };

  const isAnyLoading = loadingAll || loadingField !== null;

  return (
    <div className="rounded-lg bg-amber-50 border border-amber-200 mb-4">
      {/* Panel header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-amber-200">
        <div className="flex items-center gap-2">
          <svg
            className="w-4 h-4 text-amber-500 flex-shrink-0"
            aria-hidden="true"
            fill="currentColor"
            viewBox="0 0 20 20"
          >
            <path
              fillRule="evenodd"
              d="M8.485 2.495c.673-1.167 2.357-1.167 3.03 0l6.28 10.875c.673 1.167-.17 2.625-1.516 2.625H3.72c-1.347 0-2.189-1.458-1.515-2.625L8.485 2.495zM10 6a.75.75 0 01.75.75v3.5a.75.75 0 01-1.5 0v-3.5A.75.75 0 0110 6zm0 9a1 1 0 100-2 1 1 0 000 2z"
              clipRule="evenodd"
            />
          </svg>
          <p className="text-sm font-semibold text-amber-800">
            {fields.length} website update{fields.length !== 1 ? 's' : ''} pending review
          </p>
          <span className="text-xs text-amber-600">
            — a re-enrichment found new values for these fields
          </span>
        </div>
        <button
          type="button"
          disabled={isAnyLoading}
          aria-busy={loadingAll}
          onClick={handleAcceptAll}
          className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium bg-amber-600 text-white hover:bg-amber-700 transition-colors disabled:opacity-50"
        >
          {loadingAll ? (
            <span className="inline-block w-3 h-3 border-2 border-white border-t-transparent rounded-full animate-spin" />
          ) : (
            <svg className="w-3.5 h-3.5" aria-hidden="true" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
          )}
          Accept all
        </button>
      </div>

      {/* Panel-level error banner (M1) */}
      {allError && (
        <div className="px-4 py-2 bg-red-50 border-b border-red-200">
          <p className="text-xs text-red-700">{allError}</p>
        </div>
      )}

      {/* Field rows */}
      <div className="divide-y divide-amber-100">
        {fields.map((field) => {
          const override = pendingOverrides[field];
          const currentVal = currentValues[field];
          const isThisLoading = loadingField === field || loadingAll;
          const fieldError = errors[field];
          const detectedAt = parseDetectedAt(override.detected_at);

          return (
            <div key={field} className="px-4 py-3">
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-semibold text-amber-900 mb-1.5">
                    {formatFieldLabel(field)}
                  </p>

                  <div className="space-y-1">
                    {/* Current value */}
                    <div className="flex items-baseline gap-2">
                      <span className="text-xs text-gray-500 w-20 flex-shrink-0">Current:</span>
                      <span className="text-xs text-gray-700 break-words">
                        {formatValue(currentVal)}
                      </span>
                    </div>
                    {/* Website-found value */}
                    <div className="flex items-baseline gap-2">
                      <span className="text-xs text-amber-700 w-20 flex-shrink-0 font-medium">Website:</span>
                      <span className="text-xs font-medium text-amber-900 break-words">
                        {formatValue(override.gemini_value)}
                      </span>
                    </div>
                    {/* Detected date — only rendered when timestamp is valid (M3) */}
                    {detectedAt && (
                      <p className="text-xs text-gray-400 mt-0.5">
                        Detected {detectedAt.toLocaleDateString()}
                      </p>
                    )}
                  </div>

                  {fieldError && (
                    <p className="text-xs text-red-600 mt-1">{fieldError}</p>
                  )}
                </div>

                {/* Action buttons */}
                <div className="flex flex-col gap-1.5 flex-shrink-0 mt-0.5">
                  <button
                    type="button"
                    disabled={isThisLoading}
                    aria-busy={isThisLoading}
                    onClick={() => handleAccept(field)}
                    className="flex items-center gap-1 px-2.5 py-1.5 rounded text-xs font-medium text-white transition-colors disabled:opacity-50"
                    style={{ backgroundColor: accentColor }}
                    title="Replace current value with the website-found value"
                  >
                    {isThisLoading ? (
                      <span className="inline-block w-3 h-3 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    ) : (
                      <svg className="w-3 h-3" aria-hidden="true" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                    )}
                    Use website value
                  </button>
                  <button
                    type="button"
                    disabled={isThisLoading}
                    aria-busy={isThisLoading}
                    onClick={() => handleDiscard(field)}
                    className="flex items-center gap-1 px-2.5 py-1.5 rounded text-xs font-medium text-gray-600 bg-white border border-gray-200 hover:bg-gray-50 transition-colors disabled:opacity-50"
                    title="Keep the current value and dismiss this suggestion"
                  >
                    {isThisLoading ? (
                      <span className="inline-block w-3 h-3 border-2 border-gray-400 border-t-transparent rounded-full animate-spin" />
                    ) : (
                      <svg className="w-3 h-3" aria-hidden="true" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    )}
                    Keep current
                  </button>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};
